import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { gamePackAPI } from '../api';
import Leaderboard from '../components/Leaderboard';

const TeacherHost = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const { user } = useAuth();

  const [gamePacks, setGamePacks] = useState([]);
  const [selectedPackId, setSelectedPackId] = useState(searchParams.get('packId') || '');
  const [selectedPack, setSelectedPack] = useState(null);
  
  const [gameState, setGameState] = useState(null);
  const [roomCode, setRoomCode] = useState('');
  const [players, setPlayers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [evaluationData, setEvaluationData] = useState([]);
  const [gradedPairs, setGradedPairs] = useState({}); // index -> { type, bonus }
  const [leaderboard, setLeaderboard] = useState([]);
  const [soloAnswersCount, setSoloAnswersCount] = useState(0);
  const [soloResults, setSoloResults] = useState([]);
  const [deferredGrading, setDeferredGrading] = useState(false);
  const [guessCount, setGuessCount] = useState(0);
  const [isDrawGuessRound, setIsDrawGuessRound] = useState(false);
  const [overrideLoading, setOverrideLoading] = useState({});

  useEffect(() => {
    loadGamePacks();
  }, []);

  useEffect(() => {
    if (!socket) return;

    socket.on('player-joined', ({ player, totalPlayers }) => {
      setPlayers((prev) => [...prev, player]);
    });

    socket.on('player-reconnected', ({ sessionId, name }) => {
      console.log('Hráč se znovu připojil:', name);
    });

    socket.on('player-disconnected', ({ sessionId }) => {
      console.log('Hráč se odpojil:', sessionId);
    });

    socket.on('submission-received', ({ sessionId, submissionsCount }) => {
      console.log('Submission přijat:', sessionId);
    });

    socket.on('solution-received', ({ sessionId }) => {
      console.log('Řešení přijato:', sessionId);
    });

    socket.on('solo-answer-received', ({ sessionId, answersCount, totalPlayers }) => {
      setSoloAnswersCount(answersCount);
    });

    socket.on('guess-received', ({ guessCount: count }) => {
      setGuessCount(count);
    });

    return () => {
      socket.off('player-joined');
      socket.off('player-reconnected');
      socket.off('player-disconnected');
      socket.off('submission-received');
      socket.off('solution-received');
      socket.off('solo-answer-received');
      socket.off('guess-received');
    };
  }, [socket]);

  const loadGamePacks = async () => {
    try {
      const response = await gamePackAPI.getAll();
      setGamePacks(response.data);
      
      if (selectedPackId) {
        const pack = response.data.find((p) => p._id === selectedPackId);
        if (pack) setSelectedPack(pack);
      }
    } catch (error) {
      console.error('Chyba při načítání herních balíčků:', error);
    }
  };

  const handleCreateGame = () => {
    if (!selectedPack) {
      alert('Vyberte herní balíček');
      return;
    }

    socket.emit(
      'teacher:create-game',
      { teacherId: user.id, gamePack: selectedPack, deferredGrading },
      (response) => {
        if (response.success) {
          setRoomCode(response.roomCode);
          setGameState('lobby');
        } else {
          alert('Chyba při vytváření hry');
        }
      }
    );
  };

  const handleStartRound = () => {
    socket.emit('teacher:start-round', { roomCode }, (response) => {
      if (response.success) {
        if (response.isSolo) {
          setSoloAnswersCount(0);
          setSoloResults([]);
          setIsDrawGuessRound(response.roundType === 'draw-guess');
          setGameState('solo-waiting');
        } else {
          setIsDrawGuessRound(false);
          setGameState('creation');
        }
      }
    });
  };

  const handleGradeSoloRound = () => {
    socket.emit('teacher:grade-solo-round', { roomCode }, (response) => {
      if (response.success) {
        if (deferredGrading) {
          handleNextRound();
        } else {
          setSoloResults(response.results);
          setGameState('solo-results');
        }
      }
    });
  };

  const handleOverrideSoloAnswer = (sessionId, correct) => {
    setOverrideLoading((prev) => ({ ...prev, [sessionId]: true }));
    socket.emit('teacher:override-solo-answer', { roomCode, sessionId, correct }, (response) => {
      setOverrideLoading((prev) => ({ ...prev, [sessionId]: false }));
      if (response.success) {
        setSoloResults((prev) =>
          prev.map((r) =>
            r.sessionId === sessionId
              ? { ...r, correct, points: correct ? r.originalPoints || r.points || 1 : 0 }
              : r
          )
        );
      }
    });
  };

  const handleStartGuessPhase = () => {
    setGuessCount(0);
    socket.emit('teacher:start-guess-phase', { roomCode }, (response) => {
      if (response.success) {
        setGameState('guess-waiting');
      }
    });
  };

  const handleStartSolution = () => {
    socket.emit('teacher:start-solution', { roomCode }, (response) => {
      if (response.success) {
        setGameState('solution');
      }
    });
  };

  const handleStartEvaluation = () => {
    socket.emit('teacher:start-evaluation', { roomCode }, (response) => {
      if (response.success) {
        if (deferredGrading) {
          handleNextRound();
        } else if (response.isDrawGuess) {
          setEvaluationData(response.evaluationData);
          setGradedPairs({});
          setGameState('evaluation');
        } else {
          setEvaluationData(response.evaluationData);
          setGradedPairs({});
          setGameState('evaluation');
        }
      }
    });
  };

  const handleGrade = (index, creatorSessionId, solverSessionId, type, bonus = false) => {
    socket.emit(
      'teacher:grade-submission',
      { roomCode, creatorSessionId, solverSessionId, grading: { type, bonus } },
      (response) => {
        if (response.success) {
          setGradedPairs((prev) => ({
            ...prev,
            [index]: { type, bonus },
          }));
        }
      }
    );
  };

  const handleSkip = (index) => {
    setGradedPairs((prev) => ({
      ...prev,
      [index]: { type: 'skipped' },
    }));
  };

  const handleUnskip = (index) => {
    setGradedPairs((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleShowLeaderboard = () => {
    socket.emit('teacher:show-leaderboard', { roomCode }, (response) => {
      if (response.success) {
        setLeaderboard(response.leaderboard);
        setGameState('leaderboard');
      }
    });
  };

  const handleNextRound = () => {
    socket.emit('teacher:next-round', { roomCode }, (response) => {
      if (response.success) {
        if (response.finished) {
          setGameState('finished');
        } else {
          setGameState('lobby');
        }
      }
    });
  };

  const handleEndGame = () => {
    if (!window.confirm('Opravdu chcete ukončit hru?')) return;

    socket.emit('teacher:end-game', { roomCode }, (response) => {
      if (response.success) {
        navigate('/dashboard');
      }
    });
  };

  // Před vytvořením hry
  if (!roomCode) {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-3xl font-bold text-gray-800">
                Hostovat novou hru
              </h1>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-800"
              >
                ← Zpět
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vyberte herní balíček
                </label>
                <select
                  value={selectedPackId}
                  onChange={(e) => {
                    setSelectedPackId(e.target.value);
                    const pack = gamePacks.find((p) => p._id === e.target.value);
                    setSelectedPack(pack);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">-- Vyberte balíček --</option>
                  {gamePacks.map((pack) => (
                    <option key={pack._id} value={pack._id}>
                      {pack.name} ({pack.rounds.length} kol)
                    </option>
                  ))}
                </select>
              </div>

              {selectedPack && (
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="font-bold text-lg mb-2">{selectedPack.name}</h3>
                  <p className="text-gray-600 mb-4">{selectedPack.description}</p>
                  <div className="text-sm text-gray-700">
                    <strong>Počet kol:</strong> {selectedPack.rounds.length}
                  </div>
                </div>
              )}

              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deferredGrading}
                    onChange={(e) => setDeferredGrading(e.target.checked)}
                    className="mt-1 w-5 h-5 text-yellow-600 rounded focus:ring-yellow-500"
                  />
                  <div>
                    <span className="font-semibold text-gray-800">Hodnotit zpětně</span>
                    <p className="text-sm text-gray-600 mt-1">
                      Hra proběhne bez hodnocení a žebříčku. Po skončení si můžete
                      otevřít hodnotící tabulku a ohodnotit vše zpětně (i z jiného PC).
                    </p>
                  </div>
                </label>
              </div>

              <button
                onClick={handleCreateGame}
                disabled={!selectedPack}
                className="w-full bg-green-500 text-white py-4 rounded-lg font-semibold text-lg hover:bg-green-600 transition-colors disabled:bg-gray-400"
              >
                Vytvořit hru
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Lobby
  if (gameState === 'lobby') {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Čekárna
            </h1>

            <div className="bg-primary-50 border-2 border-primary-300 rounded-xl p-8 mb-8 text-center">
              <div className="text-sm text-gray-600 mb-2">Kód místnosti</div>
              <div className="text-6xl font-bold text-primary-600 tracking-widest">
                {roomCode}
              </div>
              <div className="text-sm text-gray-600 mt-4">
                Studenti se mohou připojit na: <strong>{window.location.origin}/student</strong>
              </div>
            </div>

            {deferredGrading && (
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-8 text-center">
                <span className="text-yellow-700 font-semibold">
                  ⏳ Režim zpětného hodnocení — hra proběhne bez hodnocení a žebříčku
                </span>
              </div>
            )}

            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4">
                Připojení hráči ({players.length})
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {players.map((player) => (
                  <div
                    key={player.sessionId}
                    className="bg-gray-100 px-4 py-3 rounded-lg text-center font-semibold"
                  >
                    {player.name}
                  </div>
                ))}
              </div>
              {players.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  Čeká se na hráče...
                </div>
              )}
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleStartRound}
                disabled={players.length < 2}
                className="flex-1 bg-green-500 text-white py-4 rounded-lg font-semibold text-lg hover:bg-green-600 transition-colors disabled:bg-gray-400"
              >
                Spustit kolo
              </button>
              <button
                onClick={handleEndGame}
                className="px-8 py-4 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600"
              >
                Ukončit hru
              </button>
            </div>

            {players.length < 2 && (
              <div className="mt-4 text-center text-sm text-gray-600">
                Minimálně 2 hráči jsou potřeba k zahájení
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Creation Phase
  if (gameState === 'creation') {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Fáze tvorby
            </h1>
            <div className="text-center py-12">
              <div className="text-6xl mb-4 animate-pulse-slow">✍️</div>
              <p className="text-xl text-gray-600 mb-4">
                Hráči vytvářejí své úkoly...
              </p>
              <div className="text-lg text-gray-500">
                Čeká se na všechny hráče
              </div>
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleStartSolution}
                className="flex-1 bg-blue-500 text-white py-4 rounded-lg font-semibold text-lg hover:bg-blue-600 transition-colors"
              >
                Spustit fázi řešení
              </button>
              <button
                onClick={handleEndGame}
                className="px-8 py-4 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600"
              >
                Ukončit
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Solo Waiting Phase (teacher-quiz, fill-in-code, click-the-bug)
  if (gameState === 'solo-waiting') {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Žáci odpovídají...
            </h1>
            <div className="text-center py-12">
              <div className="text-6xl mb-4 animate-pulse-slow">📝</div>
              <p className="text-xl text-gray-600 mb-4">
                Odpovědi: <span className="font-bold text-primary-600">{soloAnswersCount}</span> / {players.length}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-4 max-w-md mx-auto">
                <div
                  className="bg-primary-500 h-4 rounded-full transition-all"
                  style={{ width: `${players.length > 0 ? (soloAnswersCount / players.length) * 100 : 0}%` }}
                />
              </div>
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleGradeSoloRound}
                className="flex-1 bg-green-500 text-white py-4 rounded-lg font-semibold text-lg hover:bg-green-600 transition-colors"
              >
                Vyhodnotit odpovědi
              </button>
              <button
                onClick={handleEndGame}
                className="px-8 py-4 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600"
              >
                Ukončit
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Solo Results Phase
  if (gameState === 'solo-results') {
    const correctCount = soloResults.filter((r) => r.correct).length;
    const totalCount = soloResults.length;

    const formatAnswer = (result) => {
      if (!result.answer) return null;
      const a = result.answer;
      if (a.imageData) return '🖼️ kresba';
      if (a.selectedLine !== undefined) return `Řádek ${a.selectedLine}`;
      if (a.selectedOption !== undefined) return a.selectedOption === true ? 'Pravda' : a.selectedOption === false ? 'Nepravda' : String(a.selectedOption);
      if (Array.isArray(a.sortOrder)) return a.sortOrder.join(' → ');
      if (Array.isArray(a.fillAnswers)) return a.fillAnswers.join(', ');
      if (a.answer !== undefined) return String(a.answer);
      return '—';
    };

    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">
              Výsledky kola
            </h1>

            <div className="text-center mb-6">
              <div className="text-lg text-gray-600">
                Správně: <span className="font-bold text-green-600">{correctCount}</span> / {totalCount}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 max-w-md mx-auto mt-2">
                <div
                  className="bg-green-500 h-4 rounded-full transition-all"
                  style={{ width: `${totalCount > 0 ? (correctCount / totalCount) * 100 : 0}%` }}
                />
              </div>
            </div>

            <div className="space-y-3 mb-8">
              {soloResults.map((result, i) => {
                const answerText = formatAnswer(result);
                const loading = overrideLoading[result.sessionId];
                return (
                  <div
                    key={i}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg border-2 ${
                      result.correct
                        ? 'border-green-300 bg-green-50'
                        : result.answer
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-xl">{result.correct ? '✅' : result.answer ? '❌' : '⏰'}</span>
                      <span className="font-semibold text-gray-800">{result.name}</span>
                      {answerText ? (
                        <span className="font-mono bg-white px-2 py-1 rounded border text-sm truncate max-w-xs">
                          {answerText}
                        </span>
                      ) : (
                        <span className="italic text-gray-400 text-sm">Neodevzdáno</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <span className={`font-bold text-sm ${result.correct ? 'text-green-600' : 'text-gray-400'}`}>
                        +{result.points}
                      </span>
                      {result.answer && (
                        result.correct ? (
                          <button
                            disabled={loading}
                            onClick={() => handleOverrideSoloAnswer(result.sessionId, false)}
                            className="text-xs bg-red-100 text-red-600 border border-red-300 px-2 py-1 rounded hover:bg-red-200 disabled:opacity-50"
                          >
                            {loading ? '...' : 'Označit ❌'}
                          </button>
                        ) : (
                          <button
                            disabled={loading}
                            onClick={() => handleOverrideSoloAnswer(result.sessionId, true)}
                            className="text-xs bg-green-100 text-green-600 border border-green-300 px-2 py-1 rounded hover:bg-green-200 disabled:opacity-50"
                          >
                            {loading ? '...' : 'Označit ✅'}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-4">
              {isDrawGuessRound ? (
                <button
                  onClick={handleStartGuessPhase}
                  className="flex-1 bg-purple-500 text-white py-4 rounded-lg font-semibold text-lg hover:bg-purple-600"
                >
                  🎨 Spustit tipování
                </button>
              ) : (
                <button
                  onClick={handleShowLeaderboard}
                  className="flex-1 bg-primary-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-primary-700"
                >
                  Zobrazit žebříček
                </button>
              )}
              <button
                onClick={handleEndGame}
                className="px-8 py-4 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600"
              >
                Ukončit
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Guess Waiting Phase (draw-guess)
  if (gameState === 'guess-waiting') {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Žáci tipují kresby...
            </h1>
            <div className="text-center py-12">
              <div className="text-6xl mb-4 animate-pulse-slow">🎨</div>
              <p className="text-xl text-gray-600 mb-4">
                Tipy: <span className="font-bold text-purple-600">{guessCount}</span> / {players.length}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-4 max-w-md mx-auto">
                <div
                  className="bg-purple-500 h-4 rounded-full transition-all"
                  style={{ width: `${players.length > 0 ? (guessCount / players.length) * 100 : 0}%` }}
                />
              </div>
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleStartEvaluation}
                className="flex-1 bg-purple-500 text-white py-4 rounded-lg font-semibold text-lg hover:bg-purple-600 transition-colors"
              >
                Zobrazit výsledky tipování
              </button>
              <button
                onClick={handleEndGame}
                className="px-8 py-4 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600"
              >
                Ukončit
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Solution Phase
  if (gameState === 'solution') {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Fáze řešení
            </h1>
            <div className="text-center py-12">
              <div className="text-6xl mb-4 animate-pulse-slow">🤔</div>
              <p className="text-xl text-gray-600 mb-4">
                Hráči řeší přiřazené úkoly...
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleStartEvaluation}
                className="flex-1 bg-purple-500 text-white py-4 rounded-lg font-semibold text-lg hover:bg-purple-600 transition-colors"
              >
                Spustit hodnocení
              </button>
              <button
                onClick={handleEndGame}
                className="px-8 py-4 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600"
              >
                Ukončit
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Evaluation Phase
  if (gameState === 'evaluation') {
    const gradedCount = Object.values(gradedPairs).filter((g) => g.type !== 'skipped').length;
    const skippedCount = Object.values(gradedPairs).filter((g) => g.type === 'skipped').length;
    const totalPairs = evaluationData.length;

    const GRADE_OPTIONS = [
      { type: 'both', bonus: false, label: 'Oba správně', detail: '+1, +1', color: 'bg-green-500 hover:bg-green-600', icon: '✅' },
      { type: 'solver-only', bonus: false, label: 'Jen řešitel', detail: '+1', color: 'bg-blue-500 hover:bg-blue-600', icon: '👤' },
      { type: 'creator-only', bonus: false, label: 'Jen tvůrce', detail: '+1', color: 'bg-yellow-500 hover:bg-yellow-600', icon: '✨' },
      { type: 'none', bonus: false, label: 'Žádný', detail: '0, 0', color: 'bg-gray-500 hover:bg-gray-600', icon: '❌' },
      { type: 'penalty', bonus: false, label: 'Penalizace', detail: '-1', color: 'bg-red-500 hover:bg-red-600', icon: '🚫' },
      { type: 'both', bonus: true, label: 'Oba + bonus', detail: '+1.5, +1.5', color: 'bg-purple-500 hover:bg-purple-600', icon: '⭐' },
    ];

    return (
      <div className="min-h-screen p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-3xl font-bold text-gray-800">Hodnocení</h1>
              <div className="text-sm text-gray-600">
                <span className="font-bold text-green-600">{gradedCount}</span> ohodnoceno
                {skippedCount > 0 && (
                  <span className="ml-2">
                    | <span className="font-bold text-yellow-600">{skippedCount}</span> přeskočeno
                  </span>
                )}
                {' '}/ {totalPairs} celkem
              </div>
            </div>

            <div className="space-y-8 mb-8">
              {evaluationData.map((pair, index) => {
                const graded = gradedPairs[index];
                const isGraded = graded && graded.type !== 'skipped';
                const isSkipped = graded && graded.type === 'skipped';

                return (
                  <div
                    key={index}
                    className={`border-2 rounded-xl p-6 transition-colors ${
                      isGraded
                        ? 'border-green-300 bg-green-50'
                        : isSkipped
                        ? 'border-yellow-300 bg-yellow-50'
                        : 'border-gray-200'
                    }`}
                  >
                    {/* Header - jména */}
                    <div className="grid grid-cols-2 gap-6 mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{pair.isDrawGuess ? '🎨' : '✍️'}</span>
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wide">{pair.isDrawGuess ? 'Kreslič' : 'Tvůrce'}</div>
                          <div className="font-bold text-lg text-gray-800">{pair.creatorName}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🧩</span>
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wide">{pair.isDrawGuess ? 'Tipující' : 'Řešitel'}</div>
                          <div className="font-bold text-lg text-gray-800">{pair.solverName}</div>
                        </div>
                      </div>
                    </div>

                    {/* Draw-guess: kresba + tip */}
                    {pair.isDrawGuess ? (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                        <div>
                          <div className="text-sm font-semibold text-gray-600 mb-2">Kresba (zadání: {pair.drawPrompt || '—'}):</div>
                          {pair.drawing?.imageData ? (
                            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                              <img src={pair.drawing.imageData} alt="Kresba" className="w-full" />
                            </div>
                          ) : (
                            <div className="bg-gray-100 p-6 rounded-lg text-center text-gray-400 italic">Neodevzdáno</div>
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-600 mb-2">Tip žáka:</div>
                          {pair.guess ? (
                            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-lg">
                              {pair.guess}
                            </div>
                          ) : (
                            <div className="bg-gray-100 p-6 rounded-lg text-center text-gray-400 italic">Neodevzdáno</div>
                          )}
                        </div>
                      </div>
                    ) : (
                    /* Kódy a odpovědi */
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                      {/* Tvůrcův kód */}
                      <div>
                        <div className="text-sm font-semibold text-gray-600 mb-2">Zadání (kód tvůrce):</div>
                        {pair.submission?.code && (
                          <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap max-h-64 overflow-y-auto font-mono">
                            {pair.submission.code}
                          </pre>
                        )}
                        {pair.submission?.description && (
                          <div className="bg-gray-100 p-3 rounded-lg text-sm mt-2">
                            {pair.submission.description}
                          </div>
                        )}
                        {!pair.submission?.code && !pair.submission?.description && (
                          <div className="bg-gray-100 p-3 rounded-lg text-sm text-gray-400 italic">
                            Neodevzdáno
                          </div>
                        )}
                      </div>

                      {/* Řešitelova odpověď */}
                      <div>
                        <div className="text-sm font-semibold text-gray-600 mb-2">Odpověď řešitele:</div>
                        {pair.solution?.imageData && (
                          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                            <img src={pair.solution.imageData} alt="Kresba" className="w-full" />
                          </div>
                        )}
                        {pair.solution?.code && (
                          <pre className="bg-gray-900 text-blue-400 p-4 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap max-h-64 overflow-y-auto font-mono">
                            {pair.solution.code}
                          </pre>
                        )}
                        {pair.solution?.text && (
                          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm">
                            {pair.solution.text}
                          </div>
                        )}
                        {!pair.solution?.code && !pair.solution?.text && !pair.solution?.imageData && (
                          <div className="bg-gray-100 p-3 rounded-lg text-sm text-gray-400 italic">
                            Neodevzdáno
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    {/* Hodnocení */}
                    {isGraded ? (
                      <div className="flex items-center justify-between bg-green-100 border border-green-300 rounded-lg px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-green-600 font-semibold">
                            Ohodnoceno: {GRADE_OPTIONS.find(
                              (o) => o.type === graded.type && o.bonus === (graded.bonus || false)
                            )?.label || graded.type}
                          </span>
                          <span className="text-green-500 text-sm">
                            ({GRADE_OPTIONS.find(
                              (o) => o.type === graded.type && o.bonus === (graded.bonus || false)
                            )?.detail})
                          </span>
                        </div>
                        <button
                          onClick={() => handleUnskip(index)}
                          className="text-sm text-gray-500 hover:text-gray-700 underline"
                        >
                          Změnit hodnocení
                        </button>
                      </div>
                    ) : isSkipped ? (
                      <div className="flex items-center justify-between bg-yellow-100 border border-yellow-300 rounded-lg px-4 py-3">
                        <span className="text-yellow-700 font-semibold">⏭️ Přeskočeno</span>
                        <button
                          onClick={() => handleUnskip(index)}
                          className="text-sm bg-yellow-500 text-white px-4 py-1 rounded hover:bg-yellow-600"
                        >
                          Vrátit se k hodnocení
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {GRADE_OPTIONS.map((opt) => (
                          <button
                            key={`${opt.type}-${opt.bonus}`}
                            onClick={() =>
                              handleGrade(index, pair.creatorSessionId, pair.solverSessionId, opt.type, opt.bonus)
                            }
                            className={`flex-1 min-w-[140px] ${opt.color} text-white px-3 py-2 rounded-lg font-semibold text-sm transition-colors`}
                          >
                            {opt.icon} {opt.label} ({opt.detail})
                          </button>
                        ))}
                        <button
                          onClick={() => handleSkip(index)}
                          className="flex-1 min-w-[140px] bg-white border-2 border-gray-300 text-gray-600 px-3 py-2 rounded-lg font-semibold text-sm hover:bg-gray-50 transition-colors"
                        >
                          ⏭️ Přeskočit
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleShowLeaderboard}
                className="flex-1 bg-primary-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-primary-700"
              >
                Zobrazit žebříček
                {skippedCount > 0 && ` (${skippedCount} přeskočeno)`}
              </button>
              {skippedCount > 0 && (
                <button
                  onClick={() => {
                    setGradedPairs((prev) => {
                      const next = { ...prev };
                      Object.keys(next).forEach((key) => {
                        if (next[key].type === 'skipped') delete next[key];
                      });
                      return next;
                    });
                  }}
                  className="px-8 py-4 bg-yellow-500 text-white rounded-lg font-semibold hover:bg-yellow-600"
                >
                  Zobrazit přeskočené
                </button>
              )}
              <button
                onClick={handleEndGame}
                className="px-8 py-4 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600"
              >
                Ukončit
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Leaderboard
  if (gameState === 'leaderboard') {
    const hasSkipped = Object.values(gradedPairs).some((g) => g.type === 'skipped');
    const hasUngraded = evaluationData.length > 0 && Object.keys(gradedPairs).length < evaluationData.length;

    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <Leaderboard leaderboard={leaderboard} />
          <div className="mt-8 flex gap-4">
            {(hasSkipped || hasUngraded) && (
              <button
                onClick={() => setGameState('evaluation')}
                className="px-8 py-4 bg-yellow-500 text-white rounded-lg font-semibold hover:bg-yellow-600"
              >
                Zpět k hodnocení
              </button>
            )}
            <button
              onClick={handleNextRound}
              className="flex-1 bg-green-500 text-white py-4 rounded-lg font-semibold text-lg hover:bg-green-600"
            >
              Další kolo
            </button>
            <button
              onClick={handleEndGame}
              className="px-8 py-4 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600"
            >
              Ukončit hru
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Finished
  if (gameState === 'finished') {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              {deferredGrading ? '📋 Hra dokončena!' : '🎉 Hra skončila! 🎉'}
            </h1>
            <p className="text-xl text-gray-600">
              {deferredGrading
                ? 'Všechna data jsou uložena. Hodnocení najdete v dashboardu v sekci „Odehrané hry".'
                : 'Výborná práce všem účastníkům!'}
            </p>
          </div>
          {!deferredGrading && <Leaderboard leaderboard={leaderboard} />}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary-700"
            >
              Zpět na dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <div className="min-h-screen flex items-center justify-center text-white">Načítání...</div>;
};

export default TeacherHost;
